import { useEffect, useMemo, useRef, useState } from 'react'
import '../../css/VerifyOtp.css'
import { API_URL } from '../utils/Constants/URLS'
import { Link, useLocation } from 'react-router-dom'

export default function VerifyOtp ({ length = 6, onSuccess }) {
  // ===== OTP STATE =====
  const [digits, setDigits] = useState(Array.from({ length }, () => ''))
  const [verifying, setVerifying] = useState(false)
  const [otpError, setOtpError] = useState('')
  const [otpSuccess, setOtpSuccess] = useState(false)

  // ===== PASSWORD STATE =====
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [updating, setUpdating] = useState(false)
  const [pwdMsg, setPwdMsg] = useState('') // success or error banner text
  const [pwdOk, setPwdOk] = useState(false) // banner type
  const location = useLocation()
  const email = location?.state?.email || ''

  // ===== REFS =====
  const inputsRef = useRef([])

  // ===== DERIVED =====
  const isComplete = useMemo(() => digits.every(d => d.length === 1), [digits])
  const otpValue = useMemo(() => digits.join(''), [digits])

  // strength calc
  const strength = useMemo(() => {
    const p = password || ''
    let score = 0
    if (p.length >= 8) score++
    if (/[A-Z]/.test(p)) score++
    if (/[a-z]/.test(p)) score++
    if (/[0-9]/.test(p)) score++
    if (/[^A-Za-z0-9]/.test(p)) score++
    if (p.length >= 12) score++
    // map to 0-4 buckets
    if (score <= 1) return 'weak'
    if (score === 2) return 'fair'
    if (score === 3 || score === 4) return 'good'
    return 'strong'
  }, [password])

  const reqs = useMemo(
    () => ({
      len: password.length >= 8,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      digit: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password)
    }),
    [password]
  )

  useEffect(() => {
    inputsRef.current[0]?.focus()
  }, [])

  // ===== OTP HANDLERS =====
  const updateDigit = (idx, val) => {
    setDigits(prev => {
      const next = [...prev]
      next[idx] = val
      return next
    })
  }

  const handleChange = (idx, value) => {
    const onlyNums = value.replace(/\D/g, '')
    if (!onlyNums) {
      updateDigit(idx, '')
      return
    }
    const chars = onlyNums.split('')
    setDigits(prev => {
      const next = [...prev]
      let cursor = idx
      for (let c of chars) {
        if (cursor >= length) break
        next[cursor] = c
        cursor++
      }
      return next
    })

    requestAnimationFrame(() => {
      let nextIndex = idx
      while (nextIndex < length && inputsRef.current[nextIndex]?.value)
        nextIndex++
      if (nextIndex < length) inputsRef.current[nextIndex]?.focus()
    })
  }

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      if (digits[idx]) {
        e.preventDefault()
        updateDigit(idx, '')
      } else if (idx > 0) {
        e.preventDefault()
        inputsRef.current[idx - 1]?.focus()
        updateDigit(idx - 1, '')
      }
    }
    if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault()
      inputsRef.current[idx - 1]?.focus()
    }
    if (e.key === 'ArrowRight' && idx < length - 1) {
      e.preventDefault()
      inputsRef.current[idx + 1]?.focus()
    }
  }

  const handlePaste = (idx, e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text/plain').replace(/\D/g, '')
    if (!pasted) return

    setDigits(prev => {
      const next = [...prev]
      let cursor = idx
      for (let i = 0; i < pasted.length && cursor < length; i++) {
        next[cursor] = pasted[i] ?? ''
        cursor++
      }
      return next
    })

    requestAnimationFrame(() => {
      const nextIndex = Math.min(idx + pasted.length, length - 1)
      inputsRef.current[nextIndex]?.focus()
    })
  }

  // ===== API: VERIFY OTP =====
  const verifyOtp = async () => {
    if (!isComplete || verifying) return
    setVerifying(true)
    setOtpError('')

    try {
      const response = await fetch(`${API_URL}users/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otpValue })
      })
      if (response.ok) {
        setOtpSuccess(true)
      } else {
        const msg = await response.text().catch(() => '')
        setOtpError(msg || 'Invalid code. Please try again.')
        setDigits(Array.from({ length }, () => ''))
        inputsRef.current[0]?.focus()
      }
    } catch (err) {
      console.error('Verify OTP error:', err)
      setOtpError('Something went wrong. Please try again.')
    } finally {
      setVerifying(false)
    }
  }

  // ===== API: UPDATE PASSWORD =====
  const submitNewPassword = async e => {
    e.preventDefault()
    setPwdMsg('')
    setPwdOk(false)

    if (password !== confirmPassword) {
      setPwdMsg('Password and Confirm Password should be same')
      setPwdOk(false)
      return
    }

    try {
      setUpdating(true)

      const res = await fetch(`${API_URL}users/update-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, // from props/state
          newPassword: password, // <-- payload key changed
          otp: otpValue // <-- OTP entered earlier
        })
      })

      // if API returns JSON with { error, message, ... }
      const result = await res.json().catch(() => ({}))

      if (res.ok && result?.error === false) {
        setPwdMsg('Password updated successfully')
        setPwdOk(true)
        setPassword('')
        setConfirmPassword('')
        onSuccess?.(result)
      } else {
        setPwdMsg(result?.message || 'Failed to update password')
        setPwdOk(false)
      }
    } catch (e2) {
      setPwdMsg('Something went wrong while updating password.')
      setPwdOk(false)
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div className='verify-page'>
      <div
        className='verify-card'
        role='dialog'
        aria-labelledby='verify-title'
        aria-modal='true'
      >
        <header className='verify-header'>
          <div className='verify-icon' aria-hidden>
            <svg viewBox='0 0 24 24' className='verify-icon-svg'>
              <path d='M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zM11 7h2v2h-2V7zm0 4h2v6h-2v-6z' />
            </svg>
          </div>
          <h1 id='verify-title' className='verify-title'>
            Verify Your Identity
          </h1>
          <p className='verify-subtitle'>We've sent a verification code to</p>
          <span className='verify-email'>{email}</span>
        </header>

        {/* OTP STEP */}
        {!otpSuccess && (
          <div className='otp-container'>
            <div
              className='otp-inputs'
              role='group'
              aria-label={`Enter ${length}-digit verification code`}
            >
              {digits.map((val, idx) => (
                <input
                  key={idx}
                  type='text'
                  maxLength={1}
                  value={val}
                  ref={el => (inputsRef.current[idx] = el)}
                  onChange={e => handleChange(idx, e.target.value)}
                  onKeyDown={e => handleKeyDown(idx, e)}
                  onPaste={e => handlePaste(idx, e)}
                  className={`otp-input ${val ? 'filled' : ''}`}
                  aria-label={`Digit ${idx + 1}`}
                />
              ))}
            </div>

            <button
              className='verify-btn'
              onClick={verifyOtp}
              disabled={!isComplete || verifying}
            >
              {verifying ? 'Verifying...' : 'Verify & Reset Password'}
            </button>

            {otpError && (
              <div className='banner banner-error' role='alert'>
                ❌ {otpError}
              </div>
            )}
          </div>
        )}

        {/* PASSWORD STEP */}
        {otpSuccess && (
          <form className='cp-form' onSubmit={submitNewPassword}>
            {/* New Password */}
            <div className='cp-group'>
              <label className='cp-label' htmlFor='newPassword'>
                New Password
              </label>
              <div className='cp-input-wrap'>
                <input
                  id='newPassword'
                  type='password'
                  className='cp-input'
                  placeholder='Enter your new password'
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Confirm Password */}
            <div className='cp-group'>
              <label className='cp-label' htmlFor='confirmPassword'>
                Confirm New Password
              </label>
              <div className='cp-input-wrap'>
                <input
                  id='confirmPassword'
                  type='password'
                  className={`cp-input ${
                    confirmPassword
                      ? password === confirmPassword
                        ? 'success'
                        : 'error'
                      : ''
                  }`}
                  placeholder='Confirm your new password'
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              {/* Match status (inline, subtle) */}
              <div
                className={`cp-inline-msg ${
                  confirmPassword
                    ? password === confirmPassword
                      ? 'ok'
                      : 'err'
                    : ''
                }`}
              >
                {confirmPassword &&
                  password !== confirmPassword &&
                  '⚠️ Passwords do not match'}
                {confirmPassword &&
                  password === confirmPassword &&
                  '✓ Passwords match'}
              </div>
            </div>

            {/* Submit */}
            <button type='submit' className='cp-btn' disabled={updating}>
              {updating ? (
                <>
                  <span className='cp-loading' />
                  Updating Password...
                </>
              ) : (
                'Update Password'
              )}
            </button>

            {/* Banner message below button (optional) */}
            {pwdMsg && (
              <div
                className={`cp-banner ${
                  pwdOk ? 'cp-banner-success' : 'cp-banner-error'
                }`}
              >
                {pwdOk ? '✅ ' : '❌ '}
                {pwdMsg} <Link to='/'>Login?</Link>
              </div>
            )}
          </form>
        )}

        {/* Footer on OTP step */}
        {!otpSuccess && (
          <footer className='verify-footer'>
            <p className='resend-text'>Didn't receive the code?</p>
            <button
              type='button'
              className='resend-btn'
              onClick={() => {
                /* hook your resend */
              }}
            >
              Resend code
            </button>
          </footer>
        )}
      </div>
    </div>
  )
}
